<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import HandHelping from './icons/HandHelping.vue';
import Copy from './icons/Copy.vue';

useI18n();

interface Supporter {
  userId: Id<'users'>;
  name: string;
  email: string;
  votedAt: number;
}

interface IdeaWithSupporters {
  ideaId: Id<'ideas'>;
  title: string;
  person?: string;
  supporterCount: number;
  anonymousCount: number;
  supporters: Supporter[];
}

const props = defineProps<{
  modelValue: boolean;
  workshopId: Id<'workshops'>;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const { data: ideasWithSupporters, isPending: loading } = useConvexQuery(
  api.voteSupporters.getSupportersByIdea,
  () => ({ workshopId: props.workshopId })
);

const safeIdeas = computed(() => {
  return (ideasWithSupporters.value as IdeaWithSupporters[] | undefined) || [];
});

const copiedIdeaId = ref<string | null>(null);

const copySupporters = async (idea: IdeaWithSupporters) => {
  if (idea.supporters.length === 0) return;

  // Format: "Name" <email>
  const emailList = idea.supporters.map(s => `"${s.name}" <${s.email}>`).join(', ');

  try {
    await navigator.clipboard.writeText(emailList);
    copiedIdeaId.value = idea.ideaId;
    setTimeout(() => {
      if (copiedIdeaId.value === idea.ideaId) {
        copiedIdeaId.value = null;
      }
    }, 2000);
  } catch (e) {
    console.error('Failed to copy:', e);
  }
};

const close = () => {
  isOpen.value = false;
};

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value) {
    close();
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="close"
    >
      <div class="absolute inset-0 bg-black/50" @click="close"></div>

      <div
        class="relative bg-[#d4c4a8] rounded-lg w-full max-w-4xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden"
        style="
          background-image: url(&quot;data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='corkNoise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.6' numOctaves='5' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23corkNoise)'/%3E%3C/svg%3E&quot;);
        "
      >
        <div class="absolute inset-0 bg-black/5 pointer-events-none"></div>

        <div class="p-6 border-b border-black/10 relative z-10 bg-[#c9b896]/80">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <HandHelping class="w-6 h-6 text-blue-600" />
              <h2 class="text-xl font-bold text-gray-800">{{ $t('supporters.title') }}</h2>
            </div>
            <button
              @click="close"
              class="cursor-pointer text-gray-700 hover:text-gray-900 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <p class="text-gray-700 text-sm mt-1">{{ $t('supporters.subtitle') }}</p>
        </div>

        <div class="flex-1 overflow-y-auto p-6 space-y-4 relative z-10">
          <div v-if="loading" class="text-center py-8">
            <p class="text-gray-600">{{ $t('supporters.loading') }}</p>
          </div>

          <div v-else-if="safeIdeas.length === 0" class="text-center py-8 text-gray-600">
            {{ $t('supporters.noSupporters') }}
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="idea in safeIdeas"
              :key="idea.ideaId"
              class="rounded-lg border border-black/10 bg-white/90 overflow-hidden"
            >
              <div
                class="p-4 md:p-3 md:grid md:grid-cols-[minmax(0,260px)_1fr] md:items-center md:gap-4"
              >
                <div class="min-w-0 flex items-start justify-between gap-3 md:block">
                  <div>
                    <div class="font-semibold text-gray-800">{{ idea.title }}</div>
                    <div v-if="idea.person" class="text-gray-500 text-sm md:text-xs">
                      {{ idea.person }}
                    </div>
                  </div>
                  <div class="shrink-0 md:hidden">
                    <span
                      class="inline-flex items-center rounded-full bg-blue-50 text-blue-700 px-2.5 py-1 text-xs font-semibold"
                    >
                      {{ $t('supporters.supporterCount', idea.supporterCount) }}
                    </span>
                  </div>
                </div>
                <div class="mt-3 md:mt-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <span
                      class="hidden md:inline-flex items-center rounded-full bg-blue-50 text-blue-700 px-2.5 py-1 text-xs font-semibold"
                    >
                      {{ $t('supporters.supporterCount', idea.supporterCount) }}
                    </span>
                    <button
                      v-if="idea.supporters.length > 0"
                      @click="copySupporters(idea)"
                      class="shrink-0 inline-flex items-center p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                      :title="copiedIdeaId === idea.ideaId ? 'Copied!' : 'Copy emails'"
                    >
                      <Copy v-if="copiedIdeaId !== idea.ideaId" class="w-4 h-4" />
                      <span v-else class="text-xs font-medium text-green-600">Copied!</span>
                    </button>
                    <div
                      v-for="supporter in idea.supporters"
                      :key="supporter.userId"
                      class="inline-flex items-center px-2 py-1 bg-white rounded border border-gray-200 text-xs"
                    >
                      <span class="font-medium text-gray-800"
                        >{{ supporter.name }} ({{ supporter.email }})</span
                      >
                    </div>
                    <div
                      v-if="idea.anonymousCount > 0"
                      class="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded border border-gray-200 text-xs text-gray-600"
                    >
                      <span>+{{ idea.anonymousCount }} anonymous</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
